import { Box, Button as GenericButton, Flex, Image, Popover, PopoverContent, PopoverTrigger, Text, useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';

import { Button, ClimateActionHint } from 'components';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Dispatch, FC, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { mq } from 'styles/breakpoints';
import useBreakpoint from 'hooks/useBreakpoint';
import { rgba } from 'polished';
import { NavLink, useLocation } from 'react-router-dom';
import { isCorporateThemeSelector, isGuestContextSelector } from 'modules/app/appSlice';
import { ArrowRight } from 'components/Icons/Icons';

import AnimatedNumbers from 'components/AnimatedNumbers/AnimatedNumbers';
import { rem } from 'utils/fonts';

const PortfolioWrapper = styled(Box)`
  display: flex;
  align-items: flex-start;
  margin-top: 25px;
  flex-direction: column;
  overflow: hidden;
  height: 129px;

  ${mq.lg} {
    height: 186px;
    margin-top: 45px;
    width: 100%;
  }
`;

const ImageWrapper = styled(Box)<{ isCorporate?: boolean }>`
  ${(props): string =>
    props.isCorporate
      ? `
      position: absolute;
      top: 10px;
      width: calc(100% - 20px);
      left: 10px;
      ${mq.md} {
        width: calc(100% - 48px);
        top: 20px;
        left: 24px;
      }`
      : `
      width: 100%;
      position: absolute;
      top: 0;
      left: 0;
    ${mq.md} {
      width: calc(100% - 46px);
      top: 21px;
      left: 23px;
      cursor: pointer;
    }
  `}
`;

const PortfolioMainImage = styled(Image)`
  width: 100%;
  height: 154px;
  object-fit: cover;

  ${mq.lg} {
    height: 284px;
  }
`;

const Icons = styled(Flex)`
  position: absolute;
  flex-wrap: wrap;
  max-width: 40%;

  ${mq.sm} {
    max-width: 50%;
  }
`;

const Supporting = styled(Text)`
  font-weight: 500;
`;

const PortfolioTheme = styled(Text)`
  margin-top: 10px;
  margin-bottom: 10px;
`;

const PortfolioThemeWrapper = styled(Box)`
  display: flex;
  width: 100%;
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: 2;
  padding: 12px 10px;
  border-bottom-right-radius: 6px;
  border-bottom-left-radius: 6px;
  justify-content: flex-end;

  ${mq.lg} {
    justify-content: space-between;
  }
`;

const PortfolioDescription = styled(Text)`
  font-size: ${rem(15)};
  padding: 20px 10px;
  line-height: 1.46;
`;

const Left = styled.div`
  display: none;

  ${mq.lg} {
    display: block;
  }
`;

const Right = styled.div`
  text-align: right;

  width: 100%;
  ${mq.md} {
    width: 28%;
    min-width: 140px;
  }
`;

const Co2AmountWrapper = styled(Flex)`
  align-items: center;
  justify-content: flex-end;
  height: ${rem(29)};

  ${mq.md} {
    height: ${rem(59)};
  }
`;

const TotalImpact = styled(Text)`
  white-space: nowrap;
`;

const StyledButton = styled(GenericButton)`
  min-width: auto;
  height: auto;
  padding: 0;
`;

const DescriptionButton = styled(Button)`
  position: absolute;
  left: 15px;
  z-index: 3;
  border: none;
  border-radius: 36px;
  font-size: ${rem(13)};
  padding: 0 10px;
`;

interface Props {
  title?: string;
  imageUrl?: string;
  description: string;
  sdgs: SustainableDevelopmentGoal[];
  noLink?: boolean;
  co2: number;
  forceInfoDisplay?: boolean;
  setIsHover: Dispatch<SetStateAction<boolean>>;
}

const PortfolioInfo: FC<Props> = ({ title, imageUrl, sdgs, description, noLink, co2, setIsHover }) => {
  const { t } = useTranslation('common');
  const { isMobile, isSmallMobile } = useBreakpoint();
  const location = useLocation();
  const [descriptionShown, setDescriptionShown] = useState(false);
  const isGuest = useSelector(isGuestContextSelector);
  const urlBase = isGuest ? '/guest' : '';
  const isCorporateTheme = useSelector(isCorporateThemeSelector);
  const { colors } = useTheme();

  const co2AmountStyles = {
    desktop: {
      color: 'inherit',
      fontWeight: isCorporateTheme ? 'normal' : 'bold',
      fontFamily: 'heading',
      fontSize: isCorporateTheme ? rem(25) : rem(52),
    },
    mobile: {
      color: 'inherit',
      fontWeight: isCorporateTheme ? 'normal' : 'bold',
      fontFamily: 'heading',
      fontSize: isCorporateTheme ? rem(18) : rem(22),
    },
  };

  return (
    <>
      <PortfolioWrapper>
        {isCorporateTheme && !noLink && (
          <Flex pos="absolute" top="35px" right={[0, null, null, '24px']} zIndex={2} flexDir={isSmallMobile || sdgs.length > 6 ? 'column' : 'row'}>
            <NavLink exact to="/settings/projects">
              <Button variant="link" rightIcon={<ArrowRight />} size="linkSm" mr="24px" fontSize="xs" h="16px" color="white">
                <Text as="span" pr="8px">
                  {t('portfolio.changePortfolio')}
                </Text>
              </Button>
            </NavLink>
            <NavLink exact to={`${urlBase}/projects${location.search}`}>
              <Button variant="link" rightIcon={<ArrowRight />} size="linkSm" fontSize="xs" h="16px" color="white">
                <Text as="span" pr="8px">
                  {t('portfolio.link')}
                </Text>
              </Button>
            </NavLink>
          </Flex>
        )}

        <Icons top={isCorporateTheme ? '35px' : { base: '10px', lg: '38px' }} left={isCorporateTheme ? '35px' : { base: '20px', lg: '35px' }}>
          {sdgs.map(
            sdg =>
              sdg.logoUrl &&
              sdg.title && (
                <Popover trigger={isMobile ? 'click' : 'hover'} key={sdg.title}>
                  <PopoverTrigger>
                    <StyledButton data-testid="sdg-button" variant="ghost">
                      <Image
                        src={sdg.logoUrl}
                        maxW="30px"
                        maxH="30px"
                        mr="-10px"
                        mb="4px"
                        borderRadius="50%"
                        border={`2px solid ${colors.white}`}
                        alt={sdg.title}
                        zIndex={1}
                      />
                    </StyledButton>
                  </PopoverTrigger>
                  <ClimateActionHint title={sdg.title} number={sdg.number} subtitle={sdg.subtitle} description={sdg.affect} color={sdg.color} />
                </Popover>
              ),
          )}
        </Icons>
        <ImageWrapper onMouseEnter={(): void => setIsHover(true)} onMouseLeave={(): void => setIsHover(false)} isCorporate={isCorporateTheme}>
          {!!imageUrl && (
            <PortfolioMainImage src={`${imageUrl}?q=80&h=284&auto=format`} alt={title} borderTopLeftRadius="base" borderTopRightRadius="base" />
          )}
          <PortfolioThemeWrapper
            sx={{ color: imageUrl ? colors.white : colors.secondary[800], [mq.lg]: { padding: isCorporateTheme ? '0 15px 40px' : '0 15px 34px' } }}
            bg={`linear-gradient(180deg, ${rgba(colors.black, 0)} 0%, ${rgba(colors.black, 0.5)} 51.56%)`}
            paddingBottom={isCorporateTheme ? [0, null, null, null, '10px'] : '0'}
          >
            <Left>
              <Supporting fontSize={isCorporateTheme ? 'xs' : rem(18)} mb={isCorporateTheme ? '20px' : 0}>
                {t('portfolio.supporting')}:
              </Supporting>
              <PortfolioTheme
                fontSize={isCorporateTheme ? '2xl' : rem(30)}
                lineHeight={isCorporateTheme ? rem(24) : rem(30)}
                fontFamily="heading"
                fontWeight={isCorporateTheme ? 'normal' : 'bold'}
              >
                {title}
              </PortfolioTheme>
            </Left>
            {co2 > 0 && (
              <Right>
                <TotalImpact fontSize={isCorporateTheme ? 'xs' : 'md'}>
                  {t('portfolio.totalImpact')} (t CO<sub>2</sub>e)
                </TotalImpact>
                <Co2AmountWrapper>
                  <AnimatedNumbers digitFontSize={isCorporateTheme ? rem(24) : rem(52)} number={co2} digitStyleOverride={co2AmountStyles} />
                </Co2AmountWrapper>
              </Right>
            )}
          </PortfolioThemeWrapper>
          <Popover trigger={isMobile ? 'click' : 'hover'} isOpen={descriptionShown} placement="bottom-start">
            <PopoverTrigger>
              <DescriptionButton
                bg={rgba(colors.white, 0.25)}
                bottom={isCorporateTheme ? '15px' : '5px'}
                color="white"
                _hover={{ bg: rgba(colors.white, 0.05) }}
                onClick={(e): void => {
                  e.preventDefault();
                  setDescriptionShown(prev => !prev);
                }}
                rightIcon={descriptionShown ? <ChevronDownIcon /> : <ChevronUpIcon />}
                variant="outline"
                height={isCorporateTheme ? '30px' : '26px'}
                minW="unset"
              >
                <Text as="span" pr="10px">
                  {t('portfolio.about')}
                </Text>
              </DescriptionButton>
            </PopoverTrigger>
            <PopoverContent bg="secondary.800" color="white" borderRadius="6px" boxShadow="none">
              <PortfolioDescription p="10px" fontFamily="heading">
                {description}
              </PortfolioDescription>
            </PopoverContent>
          </Popover>
        </ImageWrapper>
      </PortfolioWrapper>
    </>
  );
};

export default PortfolioInfo;
